import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wifi, WifiOff } from 'lucide-react';

// Erst nach so vielen Millisekunden durchgehend offline erscheint der Banner.
// Im Zug oder bei schwachem WLAN kippt navigator.onLine im Sekundentakt - jeder
// Aussetzer blendete vorher sofort "Keine Verbindung" und danach "Wieder online"
// ein, ein Flackern ohne Nutzen. Kurze Aussetzer bleiben jetzt unsichtbar.
const OFFLINE_GRACE_MS = 3000;
// "Wieder online" nur kurz - und nur, wenn der Offline-Banner wirklich zu sehen war.
const BACK_ONLINE_VISIBLE_MS = 2000;

const NetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineMessage, setShowOfflineMessage] = useState(false);
  const [showBackOnline, setShowBackOnline] = useState(false);

  useEffect(() => {
    let graceTimer = null;

    const handleOnline = () => {
      setIsOnline(true);
      if (graceTimer) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
      // Nur wenn der Offline-Banner sichtbar war, gibt es das kurze "Wieder online";
      // sonst wuesste der User gar nicht, worauf sich das bezieht.
      setShowOfflineMessage((wasShown) => {
        if (wasShown) setShowBackOnline(true);
        return false;
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowBackOnline(false);
      if (graceTimer) clearTimeout(graceTimer);
      graceTimer = setTimeout(() => {
        graceTimer = null;
        if (!navigator.onLine) setShowOfflineMessage(true);
      }, OFFLINE_GRACE_MS);
    };

    // Frueher stand hier ein console.log bei jeder Verbindungsaenderung — im
    // Zug oder bei schwachem WLAN feuert das im Sekundentakt. Der Listener
    // bleibt (die Verbindungsart koennte spaeter Bildqualitaet steuern),
    // protokolliert aber nichts mehr.
    const updateConnectionInfo = () => {};

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    updateConnectionInfo();

    // Listen for connection changes
    if ('connection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection) {
        connection.addEventListener('change', updateConnectionInfo);
      }
    }

    return () => {
      if (graceTimer) clearTimeout(graceTimer);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if ('connection' in navigator) {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (connection) {
          connection.removeEventListener('change', updateConnectionInfo);
        }
      }
    };
  }, []);

  // "Wieder online" nach kurzer Zeit wieder ausblenden
  useEffect(() => {
    if (!showBackOnline) return undefined;
    const timer = setTimeout(() => setShowBackOnline(false), BACK_ONLINE_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [showBackOnline]);

  const visible = isOnline ? showBackOnline : showOfflineMessage;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          /* role="status" + aria-live: Der Verbindungsverlust ist genau die
             Art Information, die auch ansagen muss, wer den Bildschirm nicht
             sieht. Ohne das erscheint der Hinweis stumm. */
          role="status"
          aria-live="polite"
          /* Safe-Area statt festem top-20: In der nativen App lag der Hinweis
             sonst je nach Geraet unter der Dynamic Island. */
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)' }}
          className={`fixed left-1/2 z-toast -translate-x-1/2 rounded-xl px-4 py-2 shadow-token-lg ${
            isOnline
              ? 'bg-success text-success-foreground'
              : 'bg-danger text-danger-foreground'
          }`}
        >
          <div className="flex items-center gap-2">
            {isOnline ? (
              <>
                <Wifi className="w-4 h-4" aria-hidden="true" />
                <span className="text-sm font-medium">Wieder online</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4" aria-hidden="true" />
                {/* Bewusst ohne Zusage: Ob Eingaben offline erhalten bleiben,
                    haengt am jeweiligen Formular — ein pauschales "wird
                    gespeichert" waere ein Versprechen, das die App nicht
                    ueberall halten kann. */}
                <span className="text-sm font-medium">
                  Keine Verbindung — Inhalte sind eventuell nicht aktuell
                </span>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const useNetworkOptimization = () => {
  const [networkInfo, setNetworkInfo] = useState({
    isOnline: navigator.onLine,
    effectiveType: 'unknown',
    saveData: false,
    downlink: null
  });

  useEffect(() => {
    const updateNetworkInfo = () => {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      
      setNetworkInfo({
        isOnline: navigator.onLine,
        effectiveType: connection?.effectiveType || 'unknown',
        saveData: connection?.saveData || false,
        downlink: connection?.downlink || null
      });
    };

    const handleOnline = () => updateNetworkInfo();
    const handleOffline = () => updateNetworkInfo();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    updateNetworkInfo();

    if ('connection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      connection?.addEventListener('change', updateNetworkInfo);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if ('connection' in navigator) {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        connection?.removeEventListener('change', updateNetworkInfo);
      }
    };
  }, []);

  // Determine if we should use reduced quality content
  const shouldReduceQuality = () => {
    const { effectiveType, saveData, downlink } = networkInfo;
    return (
      saveData ||
      effectiveType === 'slow-2g' ||
      effectiveType === '2g' ||
      (downlink && downlink < 1.5)
    );
  };

  // Determine if we should disable animations
  const shouldDisableAnimations = () => {
    const { effectiveType, saveData } = networkInfo;
    return (
      saveData ||
      effectiveType === 'slow-2g' ||
      effectiveType === '2g' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  };

  return {
    ...networkInfo,
    shouldReduceQuality: shouldReduceQuality(),
    shouldDisableAnimations: shouldDisableAnimations()
  };
};

const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  fallbackSrc = null,
  lowQualitySrc = null,
  // Feste Maße (Okt 2026): Ohne width/height reserviert der Browser keinen
  // Platz, und das Bild schiebt beim Laden den Inhalt darunter (CLS).
  width,
  height,
  ...props 
}) => {
  const [imageSrc, setImageSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const { shouldReduceQuality, isOnline } = useNetworkOptimization();

  useEffect(() => {
    // Use low quality image for slow connections
    if (shouldReduceQuality && lowQualitySrc) {
      setImageSrc(lowQualitySrc);
    } else {
      setImageSrc(src);
    }
  }, [src, lowQualitySrc, shouldReduceQuality]);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    
    // Try fallback image
    if (fallbackSrc && imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
      setHasError(false);
      setIsLoading(true);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 bg-surface-raised animate-pulse rounded flex items-center justify-center">
          <div className="text-faint text-sm">Lädt…</div>
        </div>
      )}
      
      {hasError ? (
        <div className="absolute inset-0 bg-surface-raised rounded flex items-center justify-center">
          <div className="text-faint text-sm text-center">
            {isOnline ? 'Bild konnte nicht geladen werden' : 'Offline – Bild nicht verfügbar'}
          </div>
        </div>
      ) : (
        <img
          src={imageSrc}
          alt={alt}
          width={width}
          height={height}
          loading={props.loading || 'lazy'}
          decoding={props.decoding || 'async'}
          fetchpriority={props.fetchpriority || 'auto'}
          onLoad={handleLoad}
          onError={handleError}
          className={`${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-base ${className}`}
          {...props}
        />
      )}
    </div>
  );
};

export { NetworkStatus, useNetworkOptimization, OptimizedImage };
